import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Clock, Loader2, RefreshCw, Inbox } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';
import { formatShortDay } from '../../../lib/overOrder';
import { ApprovalDialog } from './ApprovalDialog';

/**
 * The queue: every order held above its client's ceiling, oldest delivery
 * first, with the deadline showing. An order still here at the cutoff is
 * settled automatically by the policy rather than lost.
 */
export const ApprovalsTab = ({ active = true }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openOrderId, setOpenOrderId] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await adminService.getPendingApprovals());
    } catch {
      toast.error('Failed to load the approval queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (active) load(); }, [active, load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="approvals-loading">
        <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
      </div>
    );
  }

  const orders = data?.orders || [];
  const overdue = orders.filter((o) => o.overdue).length;

  return (
    <div className="space-y-4" data-testid="approvals-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-600">
            <span className="font-semibold text-gray-900" data-testid="approvals-count">{orders.length}</span>
            {' '}order{orders.length === 1 ? '' : 's'} held
          </p>
          {overdue > 0 && (
            <Badge className="bg-red-100 text-red-800" data-testid="approvals-overdue-count">
              {overdue} past the deadline
            </Badge>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="refresh-approvals-btn">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {orders.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center" data-testid="approvals-empty">
            <Inbox className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">Nothing waiting</p>
            <p className="text-sm text-gray-500 mt-1">
              An order only lands here when a gated client orders above their own best day.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => (
            <Card
              key={order.order_id}
              className={`transition-shadow hover:shadow-md ${order.overdue ? 'border-red-200' : 'border-amber-200'}`}
              data-testid={`approval-row-${order.order_id}`}
            >
              <CardContent className="p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-gray-900">{order.order_number}</p>
                      <Badge className="bg-amber-100 text-amber-800">Pending approval</Badge>
                      {order.overdue && (
                        <Badge className="bg-red-100 text-red-800" data-testid={`approval-overdue-${order.order_id}`}>
                          Past the deadline
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 mt-1 truncate">{order.client_name}</p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                      <span>Delivery {formatShortDay(order.delivery_date) || '—'}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Decide by {formatShortDay(order.approval_deadline) || 'the cutoff'}
                      </span>
                      <span>${(order.total_amount || 0).toFixed(2)}</span>
                    </div>
                    {order.reason_label && (
                      <p className="text-xs text-gray-600 mt-1.5">
                        Their reason: <span className="font-medium">{order.reason_label}</span>
                        {order.reason_note ? ` — “${order.reason_note}”` : ''}
                      </p>
                    )}
                  </div>
                  <div className="shrink-0 sm:text-right">
                    <Button
                      className="bg-orange-600 hover:bg-orange-700"
                      size="sm"
                      onClick={() => setOpenOrderId(order.order_id)}
                      data-testid={`review-approval-${order.order_id}`}
                    >
                      Review {order.lines?.length || 0} line{(order.lines?.length || 0) === 1 ? '' : 's'}
                    </Button>
                  </div>
                </div>

                {order.lines?.length > 0 && (
                  <ul className="mt-3 pt-3 border-t space-y-1">
                    {order.lines.slice(0, 3).map((line) => (
                      <li key={line.product_id} className="flex items-center justify-between gap-2 text-xs">
                        <span className="truncate text-gray-700">{line.product_name}</span>
                        <span className="shrink-0 tabular-nums text-gray-500">
                          <span className="font-semibold text-orange-700">{line.ordered_qty}</span>
                          {' '}ordered · cap {line.cap_qty} · best day {line.peak_sold}
                        </span>
                      </li>
                    ))}
                    {order.lines.length > 3 && (
                      <li className="text-xs text-gray-400">+{order.lines.length - 3} more</li>
                    )}
                  </ul>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="flex items-start gap-2 text-xs text-gray-500">
        <AlertTriangle className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />
        <p>
          A held order never reaches the kitchen. Left undecided at the ordering cutoff, the
          policy settles it — by default it is trimmed to the cap and approved, so the order
          lands and the loss does not.
        </p>
      </div>

      <ApprovalDialog
        orderId={openOrderId}
        open={!!openOrderId}
        onOpenChange={(v) => { if (!v) setOpenOrderId(null); }}
        onResolved={() => { setOpenOrderId(null); load(); }}
      />
    </div>
  );
};

export default ApprovalsTab;
